import React, { useEffect, useMemo, useState } from 'react';

// Lib
import { shallowEqual } from 'react-redux';
import { useAppSelector } from 'lib/hooks/useDispatch';

// Components
import Button from 'components/UiKit/Button';
import ButtonList from 'components/ButtonList';
import ScrollButton from 'components/ScrollButton';
import PricesOnAsking from 'components/PricesOnAsking';
import ResultOverview from './components/ResultOverview';
import DebounceInput from 'components/UiKit/DebounceInput';
import CustomMessage from 'components/Messages/CustomMessage';
import List from 'pages/Search/pages/TextSearch/components/List';
import PaginationView from 'components/PaginationView/PaginationView';

// Hooks
import { useSearchByListOfIDsHandler } from 'pages/Search/useHandlers/useSearchListByIDs'

// Navigation
import { useNavigate } from 'react-router-dom';

// Type
import { createTabsConfig } from 'pages/Search/utils';
import { TNewDataSearchByIds } from 'pages/Search/type';

// Constants
import {  getValueSize } from 'lib/helpers';
import { sizeOptions20 } from 'constans/options';
import { typeListByIdSearch } from 'pages/Search/utils';

const resetObject = {};

export const SearchByListOfIDs = () => {
	const navigate = useNavigate();

	const {
		searchListByIdsResult,

		authorized,
		type,
		value,
		message,
		loading,
		isShowTextSearchListByIds,

		loadingDownloadSDF,

		loadingDrugsListByIdsTab,
		loadingCompoundsListByIdsTab,
		loadingImpuritiesListByIdsTab,
		loadingChemicalProbesListByIdsTab,
	} = useAppSelector(
		(store) => {
			return {
				type: store.search.searchListByIdsView.typeListById,
				value: store.search.searchListByIdsView.valueListById,
				message: store.search.searchListByIdsView.messageListById,
				loading: store.search.searchListByIdsView.loadingListById,
				isShowTextSearchListByIds: store.search.searchListByIdsView.isShowTextSearchListByIds,

				searchListByIdsResult: store.search.searchListByIdsResult,

				loadingDownloadSDF: store.search.loadingDownloadSDF,
				authorized: store.settings.user.authorized,

				loadingDrugsListByIdsTab: store.search.loadingDrugsListByIdsTab,
				loadingCompoundsListByIdsTab: store.search.loadingCompoundsListByIdsTab,
				loadingImpuritiesListByIdsTab: store.search.loadingImpuritiesListByIdsTab,
				loadingChemicalProbesListByIdsTab: store.search.loadingChemicalProbesListByIdsTab,
			};
		},
		shallowEqual
	);

	const {
		goTo,
		size,
		number,
		typeData,
		totalPages,
	} = useMemo(() => {
		const t = searchListByIdsResult?.type || '';
		return {
			typeData: t,
			size: searchListByIdsResult && t ? (searchListByIdsResult as Record<any, any>)[t]?.size : 0,
			number: searchListByIdsResult && t ? (searchListByIdsResult as Record<string, any>)[t]?.number : 0,
			totalPages: searchListByIdsResult && t ? (searchListByIdsResult as Record<string, any>)[t]?.totalPages : 0,
			goTo: searchListByIdsResult && t ? (searchListByIdsResult as Record<string, any>).searchDataKeyBoardGoTo[t]?.goTo : 1
		};
	}, [searchListByIdsResult]);

	const [isShowLoader, setIsShowLoader] = useState(false);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [navigate]);

	const {
		onChangeInput,
		onPagePress,
		onNextPressLast,
		onPrevPressLast,
		handlerSubmit,
		leftIconAction,
		handleDownloadConfirm,
		handleClickType,
		handlerSubmitGoTo,
		onKeyPress,
		handlerChangeGoTo,
		handlerChangeSize,
		rightIconAction,
	} = useSearchByListOfIDsHandler({
		goTo,
		value,
		number,
		typeData,
		totalPages,
		resetObject,
		setIsShowLoader,
	});

	const result = useMemo(
		() => {
			if (searchListByIdsResult[typeData as keyof TNewDataSearchByIds]?.content?.length) {
				const start = (size || 5) * number;
				const end = start + (size || 5);
				return searchListByIdsResult[typeData as keyof TNewDataSearchByIds]?.content.slice(start, end);
			}
			return searchListByIdsResult[typeData as keyof TNewDataSearchByIds]?.content;
		},
		[size, number, typeData, searchListByIdsResult]
	);

	const valueSize = getValueSize({
		opt: size,
		options: sizeOptions20
	});

	const configTabs = createTabsConfig({
		types: typeListByIdSearch,
		data: searchListByIdsResult,
		isShow: isShowTextSearchListByIds,

		loadingByType: isShowLoader ? {
			drugs: loadingDrugsListByIdsTab,
			compounds: loadingCompoundsListByIdsTab,
			impurities: loadingImpuritiesListByIdsTab,
			chemicalProbes: loadingChemicalProbesListByIdsTab,
		} : {},
	});

	const disableDownloadSdf = !!searchListByIdsResult[typeData as keyof TNewDataSearchByIds]?.content?.length && authorized;

	return (
		<>
			<ScrollButton />

			<div
				className="search-screen__wrapper-bar flex flex-ai-start flex-jc-center flex-ai-center-mobile flex-direction-column-mobile">
				<DebounceInput
					value={value || ''}

					isLeftIcon
					isTextarea
					isRightIcon
					minLength={3}
					isHiddenDropdown
					message={message}
					loading={loading}
					typeMessage={type}
					debounceTimeout={300}
					onChange={onChangeInput}
					leftIconAction={leftIconAction}
					placeholder="EBC ID, CAS, etc."
					rightIconAction={rightIconAction}
				/>
				<Button
					variable="info"
					label="Search"
					type="button"
					typePadding="middle"
					onPress={handlerSubmit}
					className="ml30 search-screen__search-btn"
				/>
			</div>

			<ResultOverview data={searchListByIdsResult} />

			<div className="search-screen__btn-tab flex flex-ai-center flex-jc-between mt10">
				<ButtonList
					type={typeData}
					config={configTabs}
					onClickType={handleClickType}
					className="search-screen__btn-tab flex flex-ai-center mt10 flex-direction-column-mobile"
				/>

				<Button
					variable="info"
					label="Download SDF"
					loading={loadingDownloadSDF}
					disabled={!disableDownloadSdf}
					onPress={handleDownloadConfirm}
				/>
			</div>

			<div className="full-width search-screen__tabs">
				{((totalPages || 0) > 1 && isShowTextSearchListByIds) &&
					<PaginationView
						isWhite
						isShowSelect

						goTo={goTo}
						onKeyPress={onKeyPress}
						handlerSubmitGoTo={handlerSubmitGoTo}
						handlerChangeGoTo={handlerChangeGoTo}

						number={number}
						totalPages={totalPages}
						onPagePress={onPagePress}
						onNextPressLast={onNextPressLast}
						onPrevPressLast={onPrevPressLast}

						valueSize={valueSize}
						optionsSize={sizeOptions20}
						handlerChangeSize={handlerChangeSize}
					/>}
			</div>

			<List
				isHiddeValue
				type={typeData}
				data={result?.length ? result : []}
			/>

			{((totalPages || 0) > 1 && isShowTextSearchListByIds) &&
				<div className="flex flex-ai-center flex-jc-between pt15 full-width flex-direction-column-mobile">
					<PricesOnAsking />
					<PaginationView
						isWhite

						goTo={goTo}
						onKeyPress={onKeyPress}
						handlerSubmitGoTo={handlerSubmitGoTo}
						handlerChangeGoTo={handlerChangeGoTo}

						number={number}
						totalPages={totalPages}
						onPagePress={onPagePress}
						onNextPressLast={onNextPressLast}
						onPrevPressLast={onPrevPressLast}

						valueSize={valueSize}
						optionsSize={sizeOptions20}
						handlerChangeSize={handlerChangeSize}
					/>
				</div>}

			{searchListByIdsResult[typeData as keyof TNewDataSearchByIds]?.content?.length
				? <CustomMessage
					title="Notice"
					className="mt30"
					text="Due to the dynamic change of the Restricted Product list, if the above material is included in the Restricted Product list or regulated as controlled material by the government, we will not sell it."
				/>
				: null}
		</>
	);
};

export default SearchByListOfIDs;